/**
 * iconSizing.ts – Single source of truth for deterministic icon sizing and expanded-panel consistency.
 *
 * This module defines:
 * 1. Two fixed icon size tokens (Small_Icon, Medium_Icon) with exact pixel dimensions per breakpoint band
 * 2. A responsive class-string builder that derives both width and height from the same matrix entry
 * 3. Token assignment rules that map (category, placement) → token, including special-category handling
 * 4. Consistent expanded-panel sizing constants
 *
 * All icon dimensions are derived solely from ICON_SIZE_MATRIX and the current band.
 * All expanded panels use the same sizing rules, independent of category.
 */

/**
 * Sub-task 1.1: Define types, bands, special categories, and the icon size matrix
 */

/** The two fixed icon size tokens. */
export type IconSizeToken = 'Small_Icon' | 'Medium_Icon';

/** The five responsive breakpoint bands. */
export type Band = 'base' | 'hp-sm' | 'hp-md' | 'hp-lg' | 'hp-xl';

/** All bands in order. */
export const BANDS: Band[] = ['base', 'hp-sm', 'hp-md', 'hp-lg', 'hp-xl'];

/** Health Information categories that use Small_Icon for their in-panel icons. */
export const SPECIAL_CATEGORIES = ['GCS', 'VITALS'] as const;

/**
 * The Icon Size Matrix: the single source of truth.
 * Maps each token and band to the exact fixed pixel dimension (width === height).
 *
 * Small_Icon:  base=32, hp-sm=36, hp-md=40, hp-lg=44, hp-xl=50
 * Medium_Icon: base=64, hp-sm=72, hp-md=80, hp-lg=90, hp-xl=100
 *
 * Satisfies Requirements 2.1, 2.2, and provides the reference dimensions for 2.3 and 2.4.
 */
export const ICON_SIZE_MATRIX: Record<IconSizeToken, Record<Band, number>> = {
  Small_Icon: {
    base: 32,
    'hp-sm': 36,
    'hp-md': 40,
    'hp-lg': 44,
    'hp-xl': 50,
  },
  Medium_Icon: {
    base: 64,
    'hp-sm': 72,
    'hp-md': 80,
    'hp-lg': 90,
    'hp-xl': 100,
  },
};

/**
 * Sub-task 1.2: Implement iconSizeClass(token)
 */

/**
 * Maps a band to its Tailwind responsive prefix.
 * Returns '' for 'base' (which has no prefix), and '<band>:' for others.
 */
const bandPrefix = (band: Band): string => (band === 'base' ? '' : `${band}:`);

/**
 * Builds the responsive class string for an icon size token.
 *
 * Returns a space-separated string of Tailwind utilities that size the icon
 * at each breakpoint band. Both width and height derive from the same ICON_SIZE_MATRIX entry,
 * guaranteeing a 1:1 square (Requirement 1.3, 7.3).
 *
 * Only emits fixed-pixel arbitrary values (w-[<px>px], h-[<px>px]) with no proportional,
 * percentage, viewport-relative, or container-relative units (Requirement 1.4).
 *
 * Example for Small_Icon:
 *   "w-[32px] h-[32px] hp-sm:w-[36px] hp-sm:h-[36px] hp-md:w-[40px] hp-md:h-[40px] hp-lg:w-[44px] hp-lg:h-[44px] hp-xl:w-[50px] hp-xl:h-[50px]"
 *
 * Satisfies Requirements 1.2, 1.3, 1.4, 2.5, 7.3.
 */
export const iconSizeClass = (token: IconSizeToken): string =>
  BANDS.map((band) => {
    const px = ICON_SIZE_MATRIX[token][band];
    const p = bandPrefix(band);
    return `${p}w-[${px}px] ${p}h-[${px}px]`;
  }).join(' ');

/**
 * Sub-task 1.3: Implement iconToken(category, placement)
 */

/** The placement of an icon relative to the category. */
export type IconPlacement = 'nav' | 'panel';

/**
 * Assigns an icon size token based on category and placement.
 *
 * Assignment rules:
 * - Every category-navigation ("nav") icon → Small_Icon (Requirement 3.1, 5.1)
 * - In-panel ("panel") icon for GCS or VITALS (Special_Categories) → Small_Icon (Requirement 5.2, 5.3)
 * - In-panel icon for any other category → Medium_Icon (Requirement 4.1)
 *
 * This ensures ADL and Emotions cards both resolve to Medium_Icon and emit identical class strings
 * per band (Requirement 4.3).
 *
 * Satisfies Requirements 3.1, 4.1, 5.1, 5.2, 5.3.
 */
export const iconToken = (category: string, placement: IconPlacement): IconSizeToken => {
  if (placement === 'nav') {
    return 'Small_Icon';
  }
  // For 'panel' placement: use Small for special categories, Medium otherwise.
  return (SPECIAL_CATEGORIES as readonly string[]).includes(category)
    ? 'Small_Icon'
    : 'Medium_Icon';
};

/**
 * Sub-task 1.4: Define the expanded-panel constants
 */

/**
 * Maximum content width per band for expanded panels.
 * Provides the testable, per-band record form (Requirement 6.3).
 */
export const EXPANDED_PANEL_MAX_WIDTH: Record<Band, number | 'full'> = {
  base: 'full',
  'hp-sm': 'full',
  'hp-md': 560,
  'hp-lg': 720,
  'hp-xl': 880,
};

/**
 * Minimum content width per band for expanded panels.
 * Ensures panels have a minimum usable width across breakpoints.
 */
export const EXPANDED_PANEL_MIN_WIDTH: Record<Band, number | 'full'> = {
  base: 'full',
  'hp-sm': 'full',
  'hp-md': 'full',
  'hp-lg': 400,
  'hp-xl': 550,
};

/**
 * The single class string applied to every expanded panel, regardless of category.
 *
 * Composition:
 * - mx-auto: center the panel horizontally
 * - px-4: consistent horizontal padding (16px = 4 * 4px spacing unit)
 * - overflow-y-auto: scroll content vertically when it overflows
 * - max-w-[400px] / min-w-[400px]: fixed width at 400px on standard screens
 * - hp-lg:max-h-full: cap height only on desktop (hp-lg and above)
 * - 2xl:max-w-[650px] / 2xl:min-w-[650px]: fixed width at 650px on screens >= 1400px
 *
 * On mobile/tablet (full screen mode): no height limit, content flows naturally
 * On desktop (side panel mode): height capped to available space with scrolling
 *
 * Because this single class is applied uniformly to every category's expanded content:
 * - All categories have the same panel width (Requirement 6.1)
 * - All categories have the same horizontal padding (Requirement 6.2)
 * - Content width is fixed per breakpoint (Requirement 6.4)
 * - Overflowing content scrolls inside the fixed box on desktop (Requirement 6.5)
 *
 * Satisfies Requirements 6.1, 6.2, 6.4, 6.5.
 */
export const EXPANDED_PANEL_CLASS =
  'mx-auto px-4 overflow-y-auto ' +
  'hp-lg:max-w-[400px] hp-lg:min-w-[400px] ' +
  'hp-xl:max-w-[550px] hp-xl:min-w-[550px] ' +
  'hp-lg:max-h-full ' +
  '2xl:max-w-[650px] 2xl:min-w-[650px]';
